import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOnOutlined';
import WorkIcon from '@mui/icons-material/Work';
import { Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { Job } from "../types";
import { getRelativeTime } from '../../../util/date';
import { getBackendUrl, getImagePath } from '../../../util/envFn';
import { enqueueSnackbar } from 'notistack';
import api from '../../../api';
import { useLogin } from '../../../context/Login/LoginContext';
import CustomMDPreview from '../../../components/CustomMD/CustomMDPreview';
import { useState } from 'react';
import ConfirmationDialog from '../../../components/ConfirmationDialog/ConfirmationDialog';
import { getNullableString } from '../../../util/string';
import JobCompanyIcon from './JobCompanyIcon';
type JobDetailsProps = {
    job: {
        logoUrl?: string;
        company?: string;
        postedDate?: string;
        requirements?: string[];
    } & Job | null;
    fetchJob: () => void;
}

const getCrawlerDisplay = (provider: string | undefined | null, search: string | undefined | null) => {
    return provider + (!!provider && !!search ? " - " : "") + search;
}

const JobDetails = ({ job, fetchJob }: JobDetailsProps) => {
    const { openLoginDialog, isLoggedIn } = useLogin();
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

    if (!job)
        return (
            <Card>
                <CardContent>
                    <Typography>No job details available.</Typography>
                </CardContent>
            </Card>
        );

    const onApply = () => {
        setOpenConfirmDialog(false);

        if (!isLoggedIn) {
            openLoginDialog();
            return;
        }

        api.post(getBackendUrl('/jobs/apply/' + job._id))
            .then(res => {
                enqueueSnackbar("Applied successfully", { variant: 'success' });
            })
            .catch(error => {
                if (error.response.status === 401)
                    openLoginDialog();
                else
                    enqueueSnackbar(error.response.data.message, { variant: 'error' });
            })
            .finally(() => {
                fetchJob();
            });
    }

    return (
        <Card>
            <ConfirmationDialog
                open={openConfirmDialog}
                onCancel={() => setOpenConfirmDialog(false)}
                title="Confirm"
                message="Are you sure you want to apply for this job?"
                severity="info"
                onConfirm={onApply}
            />
            <CardContent>
                <Stack gap={2} pb={4}>
                    <Stack direction="row" gap={1} justifyContent="space-between" alignItems="top">
                        <JobCompanyIcon job={job} size={100} />
                        <Box >
                            {job.fromCrawlerSearch &&
                                <Chip label={getCrawlerDisplay(job.fromCrawlerProvider, job.fromCrawlerSearch)} color="primary" />}
                        </Box>
                    </Stack>
                    {/* <JobCompanyIcon job={job} size={100} /> */}
                    <Stack gap={.5} mb={2}>
                        <Typography variant="h5">{getNullableString(job.title)}</Typography>
                        <Typography variant="subtitle2" fontWeight={400}>{getNullableString(job.company)}</Typography>
                    </Stack>
                    <Stack alignItems="start" gap={1.5}>
                        <Stack direction="row" gap={1}>
                            <LocationOnIcon fontSize="inherit" sx={{ color: 'text.secondary' }} />
                            <Stack direction="row" gap={.5} >
                                <Chip label={getNullableString(job.location)} size="small" sx={{ height: 1, fontSize: "12px" }} />
                            </Stack>
                        </Stack>
                        <Stack direction="row" gap={1} alignItems="center">
                            <WorkIcon fontSize="inherit" sx={{ color: 'text.secondary' }} />
                            <Stack direction="row" gap={.5} >
                                <Chip label={getNullableString(job.category)} size="small" sx={{ height: 1, fontSize: "12px" }} />
                            </Stack>
                        </Stack>
                        <Stack direction="row" gap={1}>
                            <AccessTimeIcon fontSize="inherit" sx={{ color: 'text.secondary' }} />
                            <Stack direction="row" gap={.5} >
                                <Chip label={getNullableString(job.type)} size="small" sx={{ height: 1, fontSize: "12px" }} />
                            </Stack>
                        </Stack>
                        {/* <Typography variant="caption" >Posted {displayValue(job.postedDate)}</Typography> */}
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Typography variant="body2" color="text.secondary">
                                {job.createdAt ? getRelativeTime(job.createdAt) : "Recently"}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Stack direction="row" gap={1}>
                        {
                            job.applied ? <Button variant="contained" color="primary" onClick={onApply} disabled>Applied</Button> :
                                <Button variant="contained" color="primary" onClick={() => setOpenConfirmDialog(true)}>Apply</Button>
                        }
                        {
                            job.externalUrl && <Button variant="outlined" color="secondary" onClick={() => window.open(job.externalUrl, '_blank')}>View Job</Button>
                        }
                    </Stack>
                    <CustomMDPreview description={job.description} />
                </Stack>
            </CardContent>
        </Card>
    );
}

export default JobDetails;  
